import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { auth, db } from './firebase'; 
import { doc, getDoc, setDoc } from 'firebase/firestore'; 
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { Toaster } from 'react-hot-toast';

// Tus componentes
import LoginScreen from './LoginScreen';
import PublicBookingView from './PublicBookingView';

// Vistas del Panel
import ClientesView from './ClientesView';
import ProyectosEnTransitoView from './ProyectosEnTransitoView';
import CalendarioView from './CalendarioView'; 
import DashboardView from './DashboardView'; 
import CentroDeControlView from './CentroDeControlView'; 
import ComprasView from './ComprasView';
import ReportesView from './ReportesView';
import Notifications from './Notifications';
import InventarioMateriaPrimaView from './InventarioMateriaPrimaView';
import InventarioAccesoriosView from './InventarioAccesoriosView';
import ConfiguracionView from './ConfiguracionView';
import SeguimientoView from './SeguimientoView';
import PresupuestosView from './PresupuestosView'; 

// 🔥 IMPORTAMOS EL NUEVO CENTRO DE OPERACIONES FINANCIERAS
import CentroOperaciones from './components/finanzas/CentroOperaciones';

// 🔥 IMPORTAMOS EL NUEVO CENTRO DE DISEÑO MULTISITIO
import CentroDeDisenoView from './CentroDeDisenoView';

// 🔥 IMPORTAMOS LOS NUEVOS COMPONENTES DE ASISTENCIA QR
import AsistenciaAdminView from './AsistenciaAdminView';
import FichajeMovilView from './FichajeMovilView';

import { FaTachometerAlt, FaUsers, FaShippingFast, FaCalendarAlt, FaChartLine, FaCog, FaSignOutAlt, FaShoppingCart, FaStream, FaBoxes, FaPuzzlePiece, FaCalculator, FaBars, FaCalendarPlus, FaWallet, FaPalette, FaUserClock, FaDownload } from 'react-icons/fa'; 
import miLogo from './assets/mi-logo.png';

function PannelDeControl({ usuario }) { 
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [activeView, setActiveView] = useState('dashboard');
    const [navigationTarget, setNavigationTarget] = useState(null);

    const handleLogout = () => { signOut(auth).catch((error) => console.error("Error:", error)); };

    const handleNavigate = (target) => {
        setActiveView(target.view);
        setNavigationTarget(target.data);
    };

    const allNavigationItems = {
        dashboard: { 
            label: 'Dashboard', 
            icon: <FaTachometerAlt />, 
            component: <DashboardView setActiveView={setActiveView} /> 
        },
        agendarCita: { label: 'Hoppy Calendar', icon: <FaCalendarPlus />, component: <CalendarioView usuario={usuario} /> }, 
        clientes: { label: 'Clientes', icon: <FaUsers />, component: <ClientesView /> },
        seguimiento: { label: 'Seguimiento', icon: <FaStream />, component: <SeguimientoView /> },
        proyectos: { label: 'Proyectos', icon: <FaShippingFast />, component: <ProyectosEnTransitoView usuario={usuario} navigationTarget={navigationTarget} onNavigationComplete={() => setNavigationTarget(null)} /> },
        presupuestos: { label: 'Presupuestos', icon: <FaCalculator />, component: <PresupuestosView usuario={usuario} /> },
        centroOperaciones: { label: 'Centro Finanzas', icon: <FaWallet />, component: <CentroOperaciones /> },
        centroDiseno: { label: 'Centro de Diseño', icon: <FaPalette />, component: <CentroDeDisenoView /> },
        compras: { label: 'Compras', icon: <FaShoppingCart />, component: <ComprasView /> },
        materiaPrima: { label: 'Materia Prima', icon: <FaBoxes />, component: <InventarioMateriaPrimaView /> },
        accesorios: { label: 'Accesorios', icon: <FaPuzzlePiece />, component: <InventarioAccesoriosView /> },
        calendario: { label: 'Calendario Entregas', icon: <FaCalendarAlt />, component: <div className="p-8">Vista de Entregas</div> },
        reportes: { label: 'Reportes', icon: <FaChartLine />, component: <ReportesView /> },
        // 🔥 INYECTADO: Nueva vista administrativa para el control de asistencia
        asistencia: { label: 'Control Asistencia', icon: <FaUserClock />, component: <AsistenciaAdminView /> },
        configuracion: { label: 'Configuración', icon: <FaCog />, component: <ConfiguracionView usuario={usuario} /> },
    };
    
    const permissions = {
        admin: ['dashboard', 'agendarCita', 'clientes', 'seguimiento', 'proyectos', 'presupuestos', 'centroOperaciones', 'centroDiseno', 'compras', 'materiaPrima', 'accesorios', 'calendario', 'reportes', 'asistencia', 'configuracion'],
        editor: ['dashboard', 'agendarCita', 'clientes', 'seguimiento', 'proyectos', 'presupuestos', 'centroOperaciones', 'centroDiseno', 'compras', 'materiaPrima', 'accesorios', 'calendario', 'reportes', 'asistencia'],
        vendedor: ['dashboard', 'agendarCita', 'clientes', 'seguimiento', 'proyectos', 'presupuestos'],
        operario: ['proyectos', 'calendario'],
    };

    const userRole = usuario?.role?.trim() || 'operario';
    const allowedKeys = permissions[userRole] || [];
    const navigationItems = allowedKeys.reduce((acc, key) => {
        if (allNavigationItems[key]) { acc[key] = allNavigationItems[key]; }
        return acc;
    }, {});
    
    const renderActiveSection = () => {
        const currentItem = navigationItems[activeView];
        if (!currentItem) {
            const defaultViewKey = allowedKeys[0] || 'dashboard';
            if (activeView !== defaultViewKey) setActiveView(defaultViewKey);
            return navigationItems[defaultViewKey]?.component || <p className="p-4 text-center text-gray-500">No tienes acceso.</p>;
        }
        return currentItem.component;
    };
    
    const activeViewTitle = navigationItems[activeView]?.label || 'Panel de Control';

    return (
        <div className="flex h-screen bg-gray-100 font-sans overflow-hidden">
            <nav className={`absolute z-30 lg:relative w-64 bg-[#2C3E50] text-white flex flex-col p-4 shadow-lg h-full transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
                <div className="flex flex-col items-center text-center mb-10 pt-4">
                    {/* 🔥 LOGO AÚN MÁS GRANDE: Aumentado a w-48 h-48 */}
                    <img src={usuario.photoURL || miLogo} alt="Logo" className="w-48 h-48 mb-4 object-contain" />
                    
                    <span className="text-xl font-bold">{usuario.name || usuario.displayName || 'Hoppy Time'}</span>
                    <span className={`text-xs font-semibold mt-2 px-3 py-1 rounded-full uppercase bg-teal-500 text-white`}>
                        {usuario.role}
                    </span>
                </div>
                <ul className="flex flex-col space-y-2 flex-1 overflow-y-auto custom-scrollbar">
                    {Object.keys(navigationItems).map((key) => {
                        const item = navigationItems[key];
                        const isActive = activeView === key;
                        return (
                            <li key={key}>
                                <button onClick={() => { setActiveView(key); setSidebarOpen(false); }} className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors duration-200 text-left ${isActive ? 'bg-[#18BC9C] font-semibold' : 'hover:bg-gray-700'}`}>
                                    <span className="w-6 text-center mr-3">{item.icon}</span>
                                    <span>{item.label}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
                <div className="mt-auto">
                    <button onClick={handleLogout} className="w-full flex items-center mt-2 px-4 py-3 bg-red-500 hover:bg-red-600 rounded-lg transition-colors duration-200 text-left">
                        <span className="w-6 text-center mr-3"><FaSignOutAlt /></span>
                        <span>Cerrar Sesión</span>
                    </button>
                </div>
            </nav>
            <div className={`fixed inset-0 bg-black opacity-50 z-20 lg:hidden ${isSidebarOpen ? 'block' : 'hidden'}`} onClick={() => setSidebarOpen(false)}></div>
            <div className="flex-1 flex flex-col min-w-0">
                <header className="bg-white shadow-sm p-4 flex justify-between items-center z-10">
                    <div className="flex items-center">
                        <button onClick={() => setSidebarOpen(true)} className="text-gray-700 lg:hidden mr-4"><FaBars size={24} /></button>
                        <h1 className="text-2xl font-bold text-gray-800">{activeViewTitle}</h1>
                    </div>
                    <Notifications />
                </header>
                <main className="flex-1 overflow-y-auto">{renderActiveSection()}</main>
            </div>
        </div>
    );
}

// --- APP PRINCIPAL ---
function App() {
    const [usuario, setUsuario] = useState(null);
    const [cargando, setCargando] = useState(true);
    
    // 🔥 ESTADOS PARA LA INSTALACIÓN PWA
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstallable, setIsInstallable] = useState(false);

    useEffect(() => {
        // Revisamos si el index.html ya atrapó el evento por nosotros
        const checkInstallable = () => {
            if (window.deferredPrompt) {
                setDeferredPrompt(window.deferredPrompt);
                setIsInstallable(true);
            }
        };

        // Ejecutar inmediatamente al montar
        checkInstallable();

        // Escuchar por si se dispara después (nuestro evento custom y el original)
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            window.deferredPrompt = e;
            checkInstallable();
        };

        window.addEventListener('app-installable', checkInstallable);
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('app-installable', checkInstallable);
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setDeferredPrompt(null);
            window.deferredPrompt = null;
            setIsInstallable(false);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (userAuth) => {
            if (userAuth) {
                const userDocRef = doc(db, 'users', userAuth.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    setUsuario({ uid: userAuth.uid, photoURL: userAuth.photoURL, ...userDocSnap.data() });
                } else {
                    const newUserProfile = { email: userAuth.email, displayName: userAuth.displayName || userAuth.email.split('@')[0], role: 'admin' };
                    await setDoc(userDocRef, newUserProfile);
                    setUsuario({ uid: userAuth.uid, photoURL: userAuth.photoURL, ...newUserProfile });
                }
            } else { setUsuario(null); }
            setCargando(false);
        });
        return unsubscribe;
    }, []);

    if (cargando) return <div className="flex justify-center items-center h-screen bg-gray-100"><h2 className="text-2xl font-bold text-gray-700">Cargando Hoppy Time...</h2></div>;

    return (
        <div>
            <Toaster position="top-center" />
            
            {/* 🔥 BOTÓN FLOTANTE PARA INSTALAR LA APP */}
            {isInstallable && (
                <button
                    onClick={handleInstallClick}
                    className="fixed bottom-6 right-6 z-50 bg-[#18BC9C] text-white px-5 py-3 rounded-full shadow-2xl flex items-center gap-2 font-bold hover:bg-teal-700 transition-all transform hover:scale-105 animate-bounce"
                >
                    <FaDownload />
                    Instalar App
                </button>
            )}

            <Routes>
                <Route path="/reserva" element={<PublicBookingView />} />
                {/* 🔥 INYECTADO: Ruta pública y móvil para escanear y poner el PIN */}
                <Route path="/fichaje" element={<FichajeMovilView />} />
                <Route path="/*" element={usuario ? <PannelDeControl usuario={usuario} /> : <LoginScreen />} />
            </Routes>
        </div>
    );
}

export default App;